import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard, Users, PawPrint, CalendarDays, ShoppingCart, Package,
  FileText, Bell, Stethoscope, Hotel, DoorOpen, Scissors, Dumbbell, Truck, Footprints,
  BarChart3, Settings, Globe, MessageCircle, CreditCard, Repeat, LifeBuoy, X,
} from "lucide-react";

// `seccion` debe coincidir exactamente con las claves de SECCIONES en
// backend/src/config/permisos.ts y frontend/src/lib/permisos.ts — de ahí
// sale si el rol actual puede ver este enlace (permisoSeccion). `tour`,
// cuando existe, debe coincidir con las claves de PASOS en
// components/tour/TourGuiado.tsx — el tour guiado busca el enlace por ese
// atributo data-tour para saber dónde dibujar el recuadro.
const items = [
  { to: "/", icon: LayoutDashboard, label: "Panel", modulo: null, soloAdmin: false, seccion: "dashboard", tour: "dashboard" },
  { to: "/clientes", icon: Users, label: "Clientes", modulo: null, soloAdmin: false, seccion: "clientes", tour: "clientes" },
  { to: "/mascotas", icon: PawPrint, label: "Mascotas", modulo: null, soloAdmin: false, seccion: "mascotas", tour: "mascotas" },
  { to: "/agenda", icon: CalendarDays, label: "Agenda", modulo: null, soloAdmin: false, seccion: "agenda", tour: "agenda" },
  { to: "/punto-de-venta", icon: ShoppingCart, label: "Punto de venta", modulo: "pos", soloAdmin: false, seccion: "punto-de-venta" },
  { to: "/inventario", icon: Package, label: "Productos y Servicios", modulo: "inventario", soloAdmin: false, seccion: "inventario", tour: "inventario" },
  { to: "/facturacion", icon: FileText, label: "Facturación", modulo: "facturacion", soloAdmin: false, seccion: "facturacion" },
  { to: "/recordatorios", icon: Bell, label: "Recordatorios", modulo: "recordatorios", soloAdmin: false, seccion: "recordatorios" },
  // Respuestas de clientes por WhatsApp: solo el administrador la ve (son
  // mensajes privados de los clientes; ver RespuestasClientes.tsx). No
  // forma parte del catálogo de permisos por rol (no tiene sentido darle
  // este acceso a un rol que no sea ADMIN todavía).
  { to: "/respuestas-clientes", icon: MessageCircle, label: "Respuestas de clientes", modulo: null, soloAdmin: true, seccion: null },
  { to: "/hospedaje", icon: Hotel, label: "Hotel / Guardería", modulo: "hospedaje", soloAdmin: false, seccion: "hospedaje" },
  { to: "/espacios", icon: DoorOpen, label: "Espacios", modulo: "hospedaje", soloAdmin: false, seccion: "espacios" },
  { to: "/grooming", icon: Scissors, label: "Estética", modulo: "grooming", soloAdmin: false, seccion: "grooming" },
  { to: "/entrenamiento", icon: Dumbbell, label: "Entrenamiento", modulo: "entrenamiento", soloAdmin: false, seccion: "entrenamiento" },
  { to: "/domicilio", icon: Truck, label: "A domicilio", modulo: "domicilio", soloAdmin: false, seccion: "domicilio" },
  { to: "/paseos", icon: Footprints, label: "Paseos", modulo: "paseos", soloAdmin: false, seccion: "paseos" },
  { to: "/reportes", icon: BarChart3, label: "Reportes", modulo: null, soloAdmin: false, seccion: "reportes" },
  { to: "/usuarios", icon: Stethoscope, label: "Usuarios", modulo: null, soloAdmin: false, seccion: "usuarios", tour: "usuarios" },
  { to: "/transferencias", icon: Repeat, label: "Transferencias", modulo: null, soloAdmin: false, seccion: "transferencias" },
  { to: "/suscripcion", icon: CreditCard, label: "Suscripción", modulo: null, soloAdmin: false, seccion: "suscripcion" },
  { to: "/soporte-tecnico", icon: LifeBuoy, label: "Soporte técnico", modulo: null, soloAdmin: false, seccion: "soporte-tecnico" },
  { to: "/configuracion", icon: Settings, label: "Configuración", modulo: null, soloAdmin: false, seccion: "configuracion", tour: "configuracion" },
];

interface SidebarProps {
  abierto: boolean;
  onCerrar: () => void;
}

export function Sidebar({ abierto, onCerrar }: SidebarProps) {
  const { tenant, usuario, permisoSeccion } = useAuth();
  const activos: string[] = tenant?.modulosActivos ?? [];
  const visibles = items.filter(
    (i) =>
      (!i.modulo || activos.includes(i.modulo)) &&
      (!i.soloAdmin || usuario?.rol === "ADMIN") &&
      (!i.seccion || permisoSeccion(i.seccion) !== "OCULTO")
  );

  // Propuesta "Corporativo azul y morado": el menú lateral pasa de ser un
  // panel blanco plano a un panel navy con la sección activa marcada como
  // una píldora morada — así se distingue de un vistazo cuál apartado está
  // abierto, y el conjunto se siente más "de software profesional" que las
  // demás pantallas (que siguen en blanco, para no perder legibilidad en
  // formularios y tablas largas).
  const contenido = (
    <>
      <div className="flex items-center gap-2.5 px-3 mb-8">
        <div className="w-9 h-9 rounded-xl bg-violet-500 flex items-center justify-center text-white font-bold shadow-[0_0_0_4px_rgba(124,58,237,0.15)]">
          <Globe size={18} />
        </div>
        <div>
          <p className="font-bold text-white leading-tight">{tenant?.nombre ?? "Mascotas Vet"}</p>
          <p className="text-xs text-blue-300/70">Panel del negocio</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-oculta">
        {visibles.map(({ to, icon: Icon, label, tour }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-tour={tour}
            onClick={onCerrar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-violet-500 text-white shadow-md shadow-violet-950/40 translate-x-0.5"
                  : "text-blue-200/80 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <>
      {/* Escritorio: menú fijo siempre visible, sin cambios de comportamiento. */}
      <aside className="hidden lg:flex flex-col w-64 bg-blue-950 h-screen sticky top-0 py-6 px-3">
        {contenido}
      </aside>

      {/* Celular / tableta: el menú se dibuja como un cajón que se desliza
          desde la izquierda sobre un fondo oscuro, y se cierra solo al tocar
          fuera de él o al elegir una sección (ver onCerrar en cada NavLink). */}
      {abierto && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-slate-900/40" onClick={onCerrar} />
          <aside className="fixed inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-blue-950 py-6 px-3 shadow-2xl">
            <button
              onClick={onCerrar}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-blue-300 hover:bg-white/10 hover:text-white"
              aria-label="Cerrar menú"
            >
              <X size={18} />
            </button>
            {contenido}
          </aside>
        </div>
      )}
    </>
  );
}
